/* eslint-disable max-lines */
// TODO: Reduce LOC of this file by breaking down into smaller, focused modules
import { Selection, select } from 'd3-selection';
import { D3Node, EdgeRenderConfig, ERDEdge } from '../../types/erd';
import { calculateCurveOffset, getCSSVar } from '../../utils/erd.utils';
import { EDGE_RENDERING } from '../../constants/erd.constant';

export function renderSelfLoop(
  container: Selection<SVGGElement, unknown, null, undefined>,
  node: D3Node,
  nodeWidth: number,
  nodeHeight: number,
  edgeCount: number,
  edges: ERDEdge[],
  config: EdgeRenderConfig,
  isOutgoing?: boolean,
  isIncoming?: boolean,
): void {
  const cx = node.x!;
  const cy = node.y!;
  const loopSize = EDGE_RENDERING.SELF_LOOP_SIZE;

  const startX = cx - nodeWidth / 2;
  const startY = cy;
  const endX = cx + nodeWidth / 2;
  const endY = cy;

  const controlY = cy - loopSize - nodeHeight / 2;
  const control1X = cx - loopSize;
  const control2X = cx + loopSize;

  const path = `M ${startX} ${startY} C ${control1X} ${controlY}, ${control2X} ${controlY}, ${endX} ${endY}`;
  const strokeWidth = (isOutgoing || isIncoming) ? EDGE_RENDERING.HIGHLIGHTED_STROKE_WIDTH : EDGE_RENDERING.DEFAULT_STROKE_WIDTH;
  const isSelected = config.selectedNodeIdRef.current !== null;
  const strokeColor = (isOutgoing || isIncoming) ? config.HIGHLIGHT_COLOR : (isSelected ? config.UNHIGHLIGHTED_COLOR : config.EDGE_COLOR);
  const markerEnd = (isOutgoing || isIncoming) ? 'url(#arrow-highlight)' : (isSelected ? 'url(#arrow-unhighlighted)' : 'url(#arrow)');

  container
    .append('path')
    .attr('d', path)
    .attr('fill', 'none')
    .attr('stroke', strokeColor)
    .attr('stroke-width', strokeWidth)
    .attr('marker-end', markerEnd);

  const badgeX = 0.125 * startX + 0.375 * control1X + 0.375 * control2X + 0.125 * endX;
  const badgeY = 0.125 * startY + 0.375 * controlY + 0.375 * controlY + 0.125 * endY;

  const edgeLabels = edges.map(e => e.label || 'Unnamed').join(', ');
  const tooltipText = `${edgeCount} relationship${edgeCount > 1 ? 's' : ''}: ${edgeLabels}`;

  addBadge(container, badgeX, badgeY, edgeCount, config, tooltipText, edges);
}

export function renderEdge(
  container: Selection<SVGGElement, unknown, null, undefined>,
  sourceNode: D3Node,
  targetNode: D3Node,
  nodeRadius: number,
  edgeCount: number,
  hasReverse: boolean,
  edges: ERDEdge[],
  config: EdgeRenderConfig,
  isOutgoing?: boolean,
  isIncoming?: boolean,
): void {
  const dx = targetNode.x! - sourceNode.x!;
  const dy = targetNode.y! - sourceNode.y!;
  const dist = Math.sqrt(dx * dx + dy * dy);
  if (dist === 0) { return; }

  const ux = dx / dist;
  const uy = dy / dist;
  const perpX = -uy;
  const perpY = ux;
  const shortenBy = nodeRadius + 8;

  const curveOffset = calculateCurveOffset(dist, hasReverse);

  const startX = sourceNode.x! + ux * shortenBy;
  const startY = sourceNode.y! + uy * shortenBy;
  const endX = targetNode.x! - ux * shortenBy;
  const endY = targetNode.y! - uy * shortenBy;

  const controlX = (sourceNode.x! + targetNode.x!) / 2 + perpX * curveOffset;
  const controlY = (sourceNode.y! + targetNode.y!) / 2 + perpY * curveOffset;

  const path = `M ${startX} ${startY} Q ${controlX} ${controlY} ${endX} ${endY}`;
  const strokeWidth = (isOutgoing || isIncoming) ? EDGE_RENDERING.HIGHLIGHTED_STROKE_WIDTH : EDGE_RENDERING.DEFAULT_STROKE_WIDTH;
  const isSelected = config.selectedNodeIdRef.current !== null;
  const strokeColor = (isOutgoing || isIncoming) ? config.HIGHLIGHT_COLOR : (isSelected ? config.UNHIGHLIGHTED_COLOR : config.EDGE_COLOR);
  const markerEnd = (isOutgoing || isIncoming) ? 'url(#arrow-highlight)' : (isSelected ? 'url(#arrow-unhighlighted)' : 'url(#arrow)');

  container
    .append('path')
    .attr('d', path)
    .attr('fill', 'none')
    .attr('stroke', strokeColor)
    .attr('stroke-width', strokeWidth)
    .attr('marker-end', markerEnd);

  const badgeX = 0.25 * startX + 0.5 * controlX + 0.25 * endX;
  const badgeY = 0.25 * startY + 0.5 * controlY + 0.25 * endY;

  const edgeLabels = edges.map(e => e.label || 'Unnamed').join(', ');
  const tooltipText = `${edgeCount} relationship${edgeCount > 1 ? 's' : ''}: ${edgeLabels}`;

  addBadge(container, badgeX, badgeY, edgeCount, config, tooltipText, edges);
}

function addBadge(
  group: Selection<SVGGElement, unknown, null, undefined>,
  x: number,
  y: number,
  count: number,
  config: EdgeRenderConfig,
  tooltipText?: string,
  edges?: ERDEdge[],
): void {
  const radius = count >= 100 ? EDGE_RENDERING.BADGE_RADIUS + 2 : count >= 10 ? EDGE_RENDERING.BADGE_RADIUS : EDGE_RENDERING.BADGE_RADIUS - 2;
  const fontSize = count >= 100 ? '9px' : EDGE_RENDERING.BADGE_FONT_SIZE;

  const badgeGroup = group.append('g')
    .attr('class', 'badge')
    .attr('transform', `translate(${x}, ${y})`)
    .style('cursor', 'pointer');

  badgeGroup
    .append('circle')
    .attr('r', radius)
    .attr('fill', config.BADGE_COLOR)
    .attr('stroke', getCSSVar('--erd-white') || '#fff')
    .attr('stroke-width', 2);

  badgeGroup
    .append('text')
    .text(count)
    .attr('text-anchor', 'middle')
    .attr('dy', '0.35em')
    .attr('fill', getCSSVar('--erd-white') || '#fff')
    .attr('font-size', fontSize)
    .attr('font-weight', 'bold')
    .style('pointer-events', 'none');

  if (tooltipText) {
    badgeGroup.on('click', function (event) {
      event.stopPropagation();

      select('.badge-tooltip-popup').remove();

      // eslint-disable-next-line no-invalid-this
      const badgeElement = this as SVGGElement;
      const rect = badgeElement.getBoundingClientRect();

      let edgeLabels: string[] = [];
      const colonIndex = tooltipText.indexOf(':');
      if (colonIndex !== -1) {
        const labelsPart = tooltipText.slice(colonIndex + 1).trim();
        if (labelsPart) {
          edgeLabels = labelsPart
            .split(',')
            .map(label => label.trim())
            .filter(label => label.length > 0);
        }
      }

      const tooltip = select('body').append('div')
        .attr('class', 'badge-tooltip-popup')
        .style('position', 'absolute')
        .style('background', getCSSVar('--erd-white') || '#fff')
        .style('border-radius', '8px')
        .style('padding', '16px')
        .style('min-width', '200px')
        .style('max-width', '300px')
        .style('box-shadow', '0 4px 12px rgba(0, 0, 0, 0.15)')
        .style('z-index', '9999')
        .style('border', `1px solid ${getCSSVar('--erd-border-medium') || '#e0e0e0'}`)
        .on('click', function () {
          event.stopPropagation();
        });

      const header = tooltip.append('div')
        .style('display', 'flex')
        .style('justify-content', 'space-between')
        .style('align-items', 'center')
        .style('margin-bottom', '12px')
        .style('padding-bottom', '8px')
        .style('border-bottom', `1px solid ${getCSSVar('--erd-border-medium') || '#e0e0e0'}`);

      header.append('div')
        .style('font-size', '13px')
        .style('font-weight', '600')
        .style('color', getCSSVar('--erd-text-primary') || '#333')
        .style('font-family', config.FONT_FAMILY)
        .text(`${count} relationship${count > 1 ? 's' : ''}`);

      const textLighter = getCSSVar('--erd-text-lighter') || '#999';
      const textPrimary = getCSSVar('--erd-text-primary') || '#333';

      header.append('button')
        .attr('class', 'close-btn')
        .style('background', 'none')
        .style('border', 'none')
        .style('font-size', '18px')
        .style('color', textLighter)
        .style('cursor', 'pointer')
        .style('padding', '0')
        .style('width', '20px')
        .style('height', '20px')
        .style('display', 'flex')
        .style('align-items', 'center')
        .style('justify-content', 'center')
        .style('line-height', '1')
        .html('&times;')
        .on('click', function () {
          select('body').on('click.badge-tooltip', null);
          tooltip.remove();
        })
        .on('mouseenter', function () {
          // eslint-disable-next-line no-invalid-this
          select(this).style('color', textPrimary);
        })
        .on('mouseleave', function () {
          // eslint-disable-next-line no-invalid-this
          select(this).style('color', textLighter);
        });

      let searchInput: HTMLInputElement | null = null;
      if (count > 5) {
        const searchContainer = tooltip.append('div')
          .style('margin-bottom', '12px');

        const inputElement = searchContainer.append('input')
          .attr('type', 'text')
          .attr('placeholder', 'Search relationships...')
          .style('width', '100%')
          .style('padding', '8px 12px')
          .style('border', `1px solid ${getCSSVar('--erd-border-color') || '#d1d5db'}`)
          .style('border-radius', '6px')
          .style('outline', 'none')
          .style('font-size', '13px')
          .style('font-family', config.FONT_FAMILY)
          .style('transition', 'border-color 0.2s')
          .on('click', function (e) {
            e.stopPropagation();
          })
          .on('focus', function () {
            // eslint-disable-next-line no-invalid-this
            select(this).style('border-color', getCSSVar('--erd-text-muted') || '#9ca3af');
          })
          .on('blur', function () {
            // eslint-disable-next-line no-invalid-this
            select(this).style('border-color', getCSSVar('--erd-border-color') || '#d1d5db');
          });

        searchInput = inputElement.node() as HTMLInputElement;
      }

      const listContainer = tooltip.append('div')
        .style('max-height', '250px')
        .style('overflow-y', 'auto')
        .style('display', 'flex')
        .style('flex-direction', 'column')
        .style('gap', '8px');

      const renderList = (filterQuery = ''): void => {
        listContainer.selectAll('*').remove();

        const filteredLabels = filterQuery.trim()
          ? edgeLabels.filter(label => label.toLowerCase().includes(filterQuery.toLowerCase()))
          : edgeLabels;

        filteredLabels.forEach((label) => {
          listContainer.append('div')
            .style('padding', '10px 12px')
            .style('background', getCSSVar('--erd-bg-light') || '#f9fafb')
            .style('border-radius', '6px')
            .style('font-size', '13px')
            .style('color', getCSSVar('--erd-text-primary') || '#333')
            .style('font-family', config.FONT_FAMILY)
            .style('cursor', 'pointer')
            .style('transition', 'all 0.2s')
            .style('border', `1px solid ${getCSSVar('--erd-border-medium') || '#e5e7eb'}`)
            .style('word-wrap', 'break-word')
            .style('overflow-wrap', 'break-word')
            .style('word-break', 'break-word')
            .text(label)
            .on('mouseenter', function () {
              // eslint-disable-next-line no-invalid-this
              select(this)
                .style('background', getCSSVar('--erd-bg-hover') || '#f3f4f6')
                .style('border-color', getCSSVar('--erd-text-muted') || '#9ca3af');
            })
            .on('mouseleave', function () {
              // eslint-disable-next-line no-invalid-this
              select(this)
                .style('background', getCSSVar('--erd-bg-light') || '#f9fafb')
                .style('border-color', getCSSVar('--erd-border-medium') || '#e5e7eb');
            })
            .on('click', function () {
              if (edges) {
                const originalIndex = edgeLabels.indexOf(label);
                const clickedEdges = edges.filter((_e, i) => i === originalIndex);
                config.setSelectedEdges(clickedEdges);
                config.setSelectedNodeId(null);
                select('body').on('click.badge-tooltip', null);
                tooltip.remove();
              }
            });
        });

        if (filteredLabels.length === 0) {
          listContainer.append('div')
            .style('padding', '20px')
            .style('text-align', 'center')
            .style('color', getCSSVar('--erd-text-muted') || '#9ca3af')
            .style('font-size', '13px')
            .style('font-family', config.FONT_FAMILY)
            .text('No matches found');
        }
      };

      renderList();

      if (searchInput) {
        searchInput.addEventListener('input', (e) => {
          const target = e.target as HTMLInputElement;
          renderList(target.value);
        });
      }

      const tooltipNode = tooltip.node() as HTMLElement;
      const tooltipRect = tooltipNode.getBoundingClientRect();
      const viewportWidth = window.innerWidth;

      let left = rect.left + rect.width / 2 - tooltipRect.width / 2;
      let top = rect.top - tooltipRect.height - 12;

      if (left < 8) left = 8;
      if (left + tooltipRect.width > viewportWidth - 8) {
        left = viewportWidth - tooltipRect.width - 8;
      }

      if (top < 8) {
        top = rect.bottom + 12;
      }

      tooltip
        .style('left', `${left}px`)
        .style('top', `${top}px`);

      setTimeout(() => {
        select('body').on('click.badge-tooltip', () => {
          select('body').on('click.badge-tooltip', null);
          tooltip.remove();
        });
      }, 0);
    });
  }
}
